import { useEffect, useState, useCallback } from "react";
import { Linking, Platform } from "react-native";
import {
  LogLevel,
  InAppMessageClickEvent,
  OneSignal,
} from "react-native-onesignal";
import InAppReview from "react-native-in-app-review";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import useDeviceType from "./useDeviceType";
import { useSurveyAnalytics } from "./useSurveyAnalytics";

type UseInAppMessageHandlerProps = {
  onOpenFeedbackSheet: () => void;
  timeNeedsWork?: number;
  firstNotificationTime?: number;
};

let hasStartedOneSignalSetup = false;

export const useInAppMessageHandler = ({
  onOpenFeedbackSheet,
  timeNeedsWork,
  firstNotificationTime,
}: UseInAppMessageHandlerProps) => {
  const { isDesktop, isMobile } = useDeviceType();
  const userInfo = useUserInfo();
  const {
    trackCloseSurveyForm,
    trackShareFeedbackNextTimeAction,
    trackCloseNeedsWorkPopup,
    trackLoveItAction,
    trackNeedsWorkAction,
    trackShareFeedbackAction,
  } = useSurveyAnalytics();
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successResponseData, setSuccessResponseData] = useState<{
    title: string;
    message: string;
  }>({
    title: "",
    message: "",
  });

  const triggerStoreReview = useCallback(async () => {
    try {
      const isAvailable = InAppReview.isAvailable();
      if (isAvailable) {
        InAppReview.RequestInAppReview()
          .then((hasFlowFinishedSuccessfully) => {
            if (hasFlowFinishedSuccessfully) {
              OneSignal.User.addTags({
                last_notification_seen: String(Math.floor(Date.now() / 1000)),
              });
            } else {
              fallbackToStore();
            }
          })
          .catch(() => {
            fallbackToStore();
          });
      } else {
        fallbackToStore();
      }
    } catch (error) {
      fallbackToStore();
    }
  }, []);

  const handleClose = useCallback(() => {
    setShowSuccessPopup(false);
  }, []);

  const handleOpenSuccessPopup = useCallback(() => {
    setShowSuccessPopup(true);
  }, []);

  const showNotificationAfterDays = useCallback(async () => {
    if (Platform.OS !== "web") {
      try {
        if (!OneSignal || !OneSignal.User) {
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));

        const tags = await OneSignal.User.getTags();
        const lastSeen = tags?.last_notification_seen;

        const needsWork = await AsyncStorage.getItem("needs_work");

        const rating_cancelled = await AsyncStorage.getItem("rating_cancelled");
        let mDiffHours =
          needsWork === "true" || rating_cancelled === "true"
            ? (timeNeedsWork ?? 14) * 24
            : 14 * 24;

        if (lastSeen) {
          const lastSeenDate = new Date(Number(lastSeen) * 1000);
          const currentDate = new Date();
          const diffTime = currentDate.getTime() - lastSeenDate.getTime();
          const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

          if (diffHours >= mDiffHours) {
            OneSignal.InAppMessages.addTrigger("reshow_notification", "true");

            OneSignal.User.addTags({
              last_notification_seen: String(Math.floor(Date.now() / 1000)),
            });
            if (needsWork === "true") {
              AsyncStorage.setItem("needs_work", "false");
            }
            if (rating_cancelled === "true") {
              AsyncStorage.setItem("rating_cancelled", "false");
            }
          }
        }
      } catch (error) {
        console.error("Error checking notification timing:", error);
      }
    }
  }, []);

  useEffect(() => {
    showNotificationAfterDays();
  }, [showNotificationAfterDays]);

  useEffect(() => {
    const updateFirstNotificationTag = async () => {
      const firstNotificationSeen = await AsyncStorage.getItem(
        "first_notification_seen"
      );

      if (!firstNotificationSeen || firstNotificationSeen !== "true") {
        AsyncStorage.getItem("first_notification_timestamp").then(
          (timestamp) => {
            const firstNotificationCheck = async () => {
              if (timestamp) {
                const firstLoginTimestamp = parseInt(timestamp, 10);
                const currentTimestamp = Math.floor(Date.now() / 1000);
                const hoursSinceFirstLogin = Math.floor(
                  (currentTimestamp - firstLoginTimestamp) / (60 * 60)
                );
                if (hoursSinceFirstLogin >= (firstNotificationTime ?? 24)) {
                  OneSignal.InAppMessages.addTrigger(
                    "reshow_notification",
                    "true"
                  );
                  await AsyncStorage.setItem("first_notification_seen", "true");
                }
              }
            };
            firstNotificationCheck();
          }
        );
      }
    };

    updateFirstNotificationTag();
  }, []);

  useEffect(() => {
    if (Platform.OS === "web" || hasStartedOneSignalSetup) {
      return;
    }

    hasStartedOneSignalSetup = true;

    const setupOneSignal = async () => {
      try {
        OneSignal.Debug.setLogLevel(LogLevel.Verbose);
        OneSignal.initialize(process.env.EXPO_PUBLIC_ONE_SIGNAL_APP_ID || "");

        const hasPermission =
          await OneSignal.Notifications.getPermissionAsync();
        if (hasPermission) {
          return;
        }

        // Don't Allow exhausts the iOS prompt. Passing true here would
        // immediately show "Open Settings", and remounts would repeat it.
        const canRequest = await OneSignal.Notifications.canRequestPermission();
        if (!canRequest) {
          return;
        }

        await OneSignal.Notifications.requestPermission(false);
      } catch (error) {
        console.error("Error initializing OneSignal:", error);
      }
    };
    setupOneSignal();
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web") {
      const initializeOneSignal = async () => {
        try {
          if (userInfo && userInfo.getUserId() > 0) {
            const email = userInfo.user?.email;
            if (email) {
              OneSignal.User.addEmail(email);
            }
            OneSignal.login(String(userInfo.getUserId()));

            OneSignal.User.addTags({
              user_id: String(userInfo.getUserId()),
              device_type: isMobile ? "mobile" : isDesktop ? "desktop" : "ipad",
              pro_user: userInfo.user?.custom_theme ? "true" : "false",
            });

            const firstNotificationTimestamp = await AsyncStorage.getItem(
              "first_notification_timestamp"
            );
            if (!firstNotificationTimestamp) {
              const firstLoginTimestamp = Math.floor(Date.now() / 1000);
              await AsyncStorage.setItem(
                "first_notification_timestamp",
                firstLoginTimestamp.toString()
              );
            }
          }

          const handleClick = async (event: InAppMessageClickEvent) => {
            try {
              const currentTimestamp = Math.floor(Date.now() / 1000);

              switch (event.result.actionId) {
                case "close_survey_form":
                  trackCloseSurveyForm({
                    timestamp: currentTimestamp,
                    action_id: event.result.actionId,
                  });
                  break;
                case "share_feedback_next_time_action":
                  trackShareFeedbackNextTimeAction({
                    timestamp: currentTimestamp,
                    action_id: event.result.actionId,
                  });
                  break;
                case "close_needs_work_popup":
                  trackCloseNeedsWorkPopup({
                    timestamp: currentTimestamp,
                    action_id: event.result.actionId,
                  });
                  break;
                case "love_it_action":
                  trackLoveItAction({
                    timestamp: currentTimestamp,
                    action_id: event.result.actionId,
                  });
                  OneSignal.User.addTags({
                    last_notification_seen: String(currentTimestamp),
                  });
                  triggerStoreReview();
                  await AsyncStorage.setItem("reshow_notification", "false");
                  break;

                case "needs_work_action":
                  trackNeedsWorkAction({
                    timestamp: currentTimestamp,
                    action_id: event.result.actionId,
                  });
                  OneSignal.User.addTags({
                    last_notification_seen: String(currentTimestamp),
                  });
                  OneSignal.InAppMessages.addTrigger("needs_work", "true");
                  await AsyncStorage.multiSet([
                    ["needs_work", "true"],
                    ["reshow_notification", "false"],
                  ]);
                  break;

                case "share_feedback_action":
                  trackShareFeedbackAction({
                    timestamp: currentTimestamp,
                    action_id: event.result.actionId,
                  });
                  onOpenFeedbackSheet();
                  break;

                default:
                  break;
              }
            } catch (error) {
              console.error("Error handling OneSignal click:", error);
            }
          };

          OneSignal.InAppMessages.addEventListener("click", handleClick);

          return () => {
            OneSignal.InAppMessages.removeEventListener("click", handleClick);
          };
        } catch (error) {
          console.error("Error initializing OneSignal:", error);
        }
      };

      let isMounted = true;
      let cleanupFn: (() => void) | undefined;
      initializeOneSignal().then((fn) => {
        if (isMounted) {
          cleanupFn = fn;
        } else {
          fn?.();
        }
      });
      return () => {
        isMounted = false;
        cleanupFn?.();
      };
    }
  }, [userInfo, onOpenFeedbackSheet, triggerStoreReview]);

  return {
    showSuccessPopup,
    setSuccessResponseData,
    successResponseData,
    handleClose,
    handleOpenSuccessPopup,
  };
};

const fallbackToStore = () => {
  const storeUrl =
    Platform.OS === "ios"
      ? "https://apps.apple.com/app/975333789"
      : "https://play.google.com/store/apps/details?id=com.vdbapp.android";

  Linking.openURL(storeUrl);
};
